import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { NavigationExtras, ParamMap, Router } from '@angular/router';
import { PlatformService } from '@rma/generic/util/environment';
import { RESPONSE } from '@rma/generic/util/server';
import { type Response } from 'express';
import { EMPTY, Observable } from 'rxjs';
import { NotFoundErrorMessage, StandardErrorMessage } from '../domain/error-message';

const HttpStatus = {
  Forbidden: 403,
  NotFound: 404,
  Error: 500,
} as const;

@Injectable({ providedIn: 'root' })
export class ErrorService {
  public constructor(
    private readonly router: Router,
    private readonly platformSerivce: PlatformService,
    @Optional() @Inject(RESPONSE) private readonly response: Response,
  ) {}

  public handleError<T>(url: string, error: HttpErrorResponse): Observable<T> {
    let errorMessage: string;

    if (this.platformSerivce.isPlatformBrowser) {
      //log this so it shows up in TrackJS
      console.error(`NAVIGATION ERROR | ${url}`, error);
    }

    let newUrl = '/internal-error';

    switch (error.status) {
      case HttpStatus.NotFound:
        errorMessage = NotFoundErrorMessage;
        newUrl = '/not-found';
        break;
      case HttpStatus.Forbidden:
        errorMessage = error.statusText ?? StandardErrorMessage;
        newUrl = '/forbidden';
        break;
      case HttpStatus.Error:
      default:
        errorMessage = `${StandardErrorMessage} ${error.message ?? ''}`;
        newUrl = '/internal-error';
        break;
    }

    const params: NavigationExtras = { skipLocationChange: true, state: { errorMessage } };

    this.router.navigate([newUrl], params);

    if (error.status && this.response) {
      this.response.status(error.status);
    }

    throw error;
  }

  public handleResolverParamError(url: string, params: ParamMap): Observable<never> {
    this.handleError(
      url,
      new HttpErrorResponse({ url, status: HttpStatus.NotFound, statusText: `missing required route params ${params.keys}` }),
    );
    return EMPTY;
  }
}
